import { Component, DestroyRef, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  API,
  AttributeDatatypeDTO,
  CompanyAttributeDatatypeDTO,
  DeviceAttributeDatatypeDTO,
  LocationAttributeDatatypeDTO,
  MediumGroupAttributeDatatypeDTO,
  ProductAttributeDatatypeDTO,
} from 'src/app/services/api/api';
import { ErrorService } from 'src/app/services/error.service';
import { UtilService } from 'src/app/services/util.service';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Permission } from 'src/app/services/api/permission';
import { QueryParamService } from '../../../services/lists/query-params';
import { PermissionService } from '../../../services/permission.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';

enum AllowedValuesCountEnum {
  ANY = 'ANY',
  EXACT_ONE = 'EXACT_ONE',
  AT_LEAST_ONE = 'AT_LEAST_ONE',
  AT_MOST_ONE = 'AT_MOST_ONE',
  CUSTOM = 'CUSTOM',
}

@Component({
  selector: 'sb-page-attribute-edit',
  templateUrl: './attribute-edit.component.html',
  styleUrls: ['./attribute-edit.component.scss'],
  standalone: false,
})
export class AttributeEditComponent implements OnInit {
  loading: boolean = true;
  attributeId: string;
  debounceTimer: ReturnType<typeof setTimeout>;
  attribute: AttributeDatatypeDTO;
  attributeType: string;
  regex: RegExp;

  countTypes: Array<keyof typeof AllowedValuesCountEnum> = Object.keys(AllowedValuesCountEnum).filter(key => isNaN(Number(key))) as Array<
    keyof typeof AllowedValuesCountEnum
  >;
  selectedType: AllowedValuesCountEnum;

  form = new UntypedFormGroup({
    ID: new UntypedFormControl('', [Validators.pattern(new RegExp('^\\S*$')), Validators.required]),
    Name: new UntypedFormControl('', [Validators.required]),
    Description: new UntypedFormControl(''),
    Type: new UntypedFormControl('', [Validators.required]),
    DefaultValue: new UntypedFormControl(null),
    RegExp: new UntypedFormControl(null),
    AdPlacerAbleToSet: new UntypedFormControl(true),
    DeviceAbleToSet: new UntypedFormControl(false),
    AskForConfirmation: new UntypedFormControl(true),
    UniqueValues: new UntypedFormControl(false),
    ExternalSynchronized: new UntypedFormControl(false),
    UsableForFilters: new UntypedFormControl(false),
    Searchable: new UntypedFormControl(false),
    DisclosedToPublic: new UntypedFormControl(false),
    Multiline: new UntypedFormControl(false),
    AllowAllowedValues: new UntypedFormControl(false),
    AllowedValues: new UntypedFormControl(''),
    AllowedValuesMinCount: new UntypedFormControl(null),
    AllowedValuesMaxCount: new UntypedFormControl(null),
  });

  get ID() {
    return this.form.get('ID');
  }

  get Name() {
    return this.form.get('Name');
  }

  get Description() {
    return this.form.get('Description');
  }

  get Type() {
    return this.form.get('Type');
  }

  get DefaultValue() {
    return this.form.get('DefaultValue');
  }

  get RegExp() {
    return this.form.get('RegExp');
  }

  get AdPlacerAbleToSet() {
    return this.form.get('AdPlacerAbleToSet');
  }

  get DeviceAbleToSet() {
    return this.form.get('DeviceAbleToSet');
  }

  get AskForConfirmation() {
    return this.form.get('AskForConfirmation');
  }

  get UniqueValues() {
    return this.form.get('UniqueValues');
  }

  get ExternalSynchronized() {
    return this.form.get('ExternalSynchronized');
  }

  get UsableForFilters() {
    return this.form.get('UsableForFilters');
  }

  get DisclosedToPublic() {
    return this.form.get('DisclosedToPublic');
  }

  get Searchable() {
    return this.form.get('Searchable');
  }

  get Multiline() {
    return this.form.get('Multiline');
  }

  get AllowAllowedValues() {
    return this.form.get('AllowAllowedValues');
  }

  /*
  get AllowedValues() {
    return this.form.get('AllowedValues');
  }
   */

  get AllowedValuesMinCount() {
    return this.form.get('AllowedValuesMinCount');
  }

  get AllowedValuesMaxCount() {
    return this.form.get('AllowedValuesMaxCount');
  }

  constructor(
    private api: API,
    private activatedRoute: ActivatedRoute,
    public util: UtilService,
    private errorHandler: ErrorService,
    private queryParamService: QueryParamService,
    public permissionService: PermissionService,
    private destroyRef: DestroyRef
  ) {
    this.loading = true;
    this.debounceTimer = null;
    this.regex = null;
    this.selectedType = AllowedValuesCountEnum.ANY;
  }

  ngOnInit(): void {
    this.activatedRoute.params.subscribe(params => {
      this.attributeType = this.queryParamService.getQueryParam('attrType');
      this.attributeId = params.attributeID;
      if (this.attributeId) {
        this.requestDebounced();
      } else {
        switch (this.attributeType) {
          case 'DeviceAttribute':
            this.attribute = this.api.createDTO('DeviceAttributeDatatypeDTO');
            break;
          case 'LocationAttribute':
            this.attribute = this.api.createDTO('LocationAttributeDatatypeDTO');
            break;
          case 'CompanyAttribute':
            this.attribute = this.api.createDTO('CompanyAttributeDatatypeDTO');
            break;
          case 'ProductAttribute':
            this.attribute = this.api.createDTO('ProductAttributeDatatypeDTO');
            break;
          case 'MediumAttribute':
            this.attribute = this.api.createDTO('MediumGroupAttributeDatatypeDTO');
            break;
          default:
            this.attribute = this.api.createDTO('AttributeDatatypeDTO');
            break;
        }
        this.loading = false;
        this.setAllowedValueInputEnabledDisabled();
      }
      if (this.attributeType) {
        this.Type.setValue(this.attributeType);
      }
    });
    this.registerFormControlChangesListener();
  }

  requestDebounced() {
    this.loading = true;
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.refreshFromServer();
    }, 500);
  }

  refreshFromServer() {
    let obs = null;

    switch (this.attributeType) {
      case 'DeviceAttribute':
        obs = this.api.ca_md_DeviceAttribute_retrieveDatatypeById(this.attributeId);
        break;
      case 'LocationAttribute':
        obs = this.api.ca_md_LocationAttribute_retrieveDatatypeById(this.attributeId);
        break;
      case 'CompanyAttribute':
        obs = this.api.ca_md_CompanyAttribute_retrieveDatatypeById(this.attributeId);
        break;
      case 'ProductAttribute':
        obs = this.api.ca_md_ProductAttribute_retrieveDatatypeById(this.attributeId);
        break;
      case 'MediumAttribute':
        obs = this.api.ca_md_MediumGroupAttribute_retrieveDatatypeById(this.attributeId);
        break;
    }

    if (obs) {
      obs
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: (data: AttributeDatatypeDTO) => {
            this.attribute = data;

            this.refreshFormFromDTO();
          },
          error: (error: any) => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  refreshFormFromDTO() {
    this.ID.setValue(this.attribute.id);
    this.Name.setValue(this.attribute.name);
    this.Description.setValue(this.attribute.description);

    this.DefaultValue.setValue(this.attribute.defaultValue);
    this.RegExp.setValue(this.attribute.forceRegEx);
    this.AdPlacerAbleToSet.setValue(this.attribute.adPlacerAbleToSet);
    this.Type.setValue(this.attributeType);
    if (this.attributeType === 'DeviceAttribute') {
      this.DeviceAbleToSet.setValue((this.attribute as DeviceAttributeDatatypeDTO).deviceAbleToSet);
    }
    this.AskForConfirmation.setValue(this.attribute.askForConfirmation);
    this.UniqueValues.setValue(this.attribute.uniqueValues);
    this.ExternalSynchronized.setValue(this.attribute.externalSynchronized);
    this.UsableForFilters.setValue(this.attribute.usableForFilters);
    this.Searchable.setValue(this.attribute.searchable);
    this.DisclosedToPublic.setValue(this.attribute.disclosedToPublic);
    this.Multiline.setValue(this.attribute.multiline);
    if (
      (!this.attribute.allowedValues || this.attribute.allowedValues.length === 0) &&
      this.attribute.allowedValuesMaxCount === null &&
      this.attribute.allowedValuesMinCount === null
    ) {
      // no Allowed Values
      this.AllowAllowedValues.setValue(false);
    } else {
      this.AllowAllowedValues.setValue(true);
    }
    // this.AllowedValues.setValue(this.attribute.allowedValues?.join(', ') ?? '');
    this.AllowedValuesMaxCount.setValue(this.attribute.allowedValuesMaxCount);
    this.AllowedValuesMinCount.setValue(this.attribute.allowedValuesMinCount);

    this.initSelectedType();

    let permissionRequired: Permission;

    switch (this.attributeType) {
      case 'DeviceAttribute':
        permissionRequired = Permission.device_attribute_datatype_edit;
        break;
      case 'LocationAttribute':
        permissionRequired = Permission.location_attribute_datatype_edit;
        break;
      case 'CompanyAttribute':
        permissionRequired = Permission.company_attribute_datatype_edit;
        break;
      case 'ProductAttribute':
        permissionRequired = Permission.product_attribute_datatype_edit;
        break;
      case 'MediumAttribute':
        permissionRequired = Permission.medium_group_attribute_datatype_edit;
        break;
    }

    this.ID.disable();
    this.Type.disable();
    this.enableDisableAllowedValuesCountInputs();
    this.setAllowedValueInputEnabledDisabled();

    if (!this.permissionService.hasPermission(permissionRequired) || this.attribute.global) {
      this.Name.disable();
      this.Description.disable();
      this.DefaultValue.disable();
      this.RegExp.disable();
      this.AdPlacerAbleToSet.disable();
      if (this.attributeType === 'DeviceAttribute') {
        this.DeviceAbleToSet.disable();
      }
      this.AskForConfirmation.disable();
      this.UniqueValues.disable();
      this.ExternalSynchronized.disable();
      this.UsableForFilters.disable();
      this.Searchable.disable();
      this.DisclosedToPublic.disable();
      this.Multiline.disable();
      this.AllowAllowedValues.disable();
      // this.AllowedValues.disable();
      this.AllowedValuesMinCount.disable();
      this.AllowedValuesMaxCount.disable();
    }
  }

  initSelectedType() {
    if (this.attribute.allowedValuesMinCount > 1 || this.attribute.allowedValuesMaxCount > 1) {
      this.selectedType = AllowedValuesCountEnum.CUSTOM;
    } else if (this.attribute.allowedValuesMinCount === 1 && this.attribute.allowedValuesMaxCount === 1) {
      this.selectedType = AllowedValuesCountEnum.EXACT_ONE;
    } else if (this.attribute.allowedValuesMinCount === 1 && !this.attribute.allowedValuesMaxCount) {
      this.selectedType = AllowedValuesCountEnum.AT_LEAST_ONE;
    } else if (!this.attribute.allowedValuesMinCount && this.attribute.allowedValuesMaxCount === 1) {
      this.selectedType = AllowedValuesCountEnum.AT_MOST_ONE;
    } else {
      this.selectedType = AllowedValuesCountEnum.ANY;
    }
  }

  setAllowedValueInputEnabledDisabled() {
    if (this.AllowAllowedValues.value) {
      // this.AllowedValues.enable();
      this.AllowedValuesMaxCount.enable();
      this.AllowedValuesMinCount.enable();
    } else {
      // this.AllowedValues.disable();
      this.AllowedValuesMaxCount.disable();
      this.AllowedValuesMinCount.disable();
    }
  }

  refreshDTOFromForm() {
    this.attribute.id = this.ID.value;
    this.attribute.name = this.Name.value;
    this.attribute.description = this.Description.value;

    this.attribute.defaultValue = this.DefaultValue.value;
    this.attribute.forceRegEx = this.RegExp.value;
    this.attribute.adPlacerAbleToSet = this.AdPlacerAbleToSet.value;
    this.attributeType = this.Type.value;
    if (this.attributeType === 'DeviceAttribute') {
      (this.attribute as DeviceAttributeDatatypeDTO).deviceAbleToSet = this.DeviceAbleToSet.value;
    }
    this.attribute.askForConfirmation = this.AskForConfirmation.value;
    this.attribute.uniqueValues = this.UniqueValues.value;
    this.attribute.externalSynchronized = this.ExternalSynchronized.value;
    this.attribute.usableForFilters = this.UsableForFilters.value;
    this.attribute.searchable = this.Searchable.value;
    this.attribute.disclosedToPublic = this.DisclosedToPublic.value;
    this.attribute.multiline = this.Multiline.value;

    if (this.AllowAllowedValues.value) {
      // this.attribute.allowedValues = this.AllowedValues.value.split(',').map(value => value.trim());
      this.attribute.allowedValuesMinCount = this.AllowedValuesMinCount.value;
      this.attribute.allowedValuesMaxCount = this.AllowedValuesMaxCount.value;
    } else {
      this.attribute.allowedValues = null;
      this.attribute.allowedValuesMinCount = null;
      this.attribute.allowedValuesMaxCount = null;
    }
  }

  submit() {
    this.loading = true;
    this.refreshDTOFromForm();
    let obs = null;

    if (this.attributeId) {
      switch (this.attributeType) {
        case 'DeviceAttribute':
          obs = this.api.ca_md_DeviceAttribute_editDatatype(this.attribute as DeviceAttributeDatatypeDTO);
          break;
        case 'LocationAttribute':
          obs = this.api.ca_md_LocationAttribute_editDatatype(this.attribute as LocationAttributeDatatypeDTO);
          break;
        case 'CompanyAttribute':
          obs = this.api.ca_md_CompanyAttribute_editDatatype(this.attribute as CompanyAttributeDatatypeDTO);
          break;
        case 'ProductAttribute':
          obs = this.api.ca_md_ProductAttribute_editDatatype(this.attribute as ProductAttributeDatatypeDTO);
          break;
        case 'MediumAttribute':
          obs = this.api.ca_md_MediumGroupAttribute_editDatatype(this.attribute as MediumGroupAttributeDatatypeDTO);
          break;
      }
    } else {
      switch (this.attributeType) {
        case 'DeviceAttribute':
          obs = this.api.ca_md_DeviceAttribute_createDatatype(this.attribute as DeviceAttributeDatatypeDTO);
          break;
        case 'LocationAttribute':
          obs = this.api.ca_md_LocationAttribute_createDatatype(this.attribute as LocationAttributeDatatypeDTO);
          break;
        case 'CompanyAttribute':
          obs = this.api.ca_md_CompanyAttribute_createDatatype(this.attribute as CompanyAttributeDatatypeDTO);
          break;
        case 'ProductAttribute':
          obs = this.api.ca_md_ProductAttribute_createDatatype(this.attribute as ProductAttributeDatatypeDTO);
          break;
        case 'MediumAttribute':
          obs = this.api.ca_md_MediumGroupAttribute_createDatatype(this.attribute as MediumGroupAttributeDatatypeDTO);
          break;
      }
    }

    if (obs) {
      obs
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: (data: AttributeDatatypeDTO) => {
            this.attribute = data;
            this.util.returnToNavigation(this.util.RoutingID.attributes);
          },
          error: (error: any) => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  cancel() {
    this.util.returnToNavigation(this.util.RoutingID.attributes);
  }

  setRegex(value: string | RegExp) {
    this.RegExp.markAsTouched();
    this.DefaultValue.clearValidators();
    if (value) {
      try {
        this.regex = new RegExp(value);
        this.RegExp.setErrors(null);
        this.DefaultValue.setValidators(Validators.pattern(this.regex));
      } catch (e) {
        this.RegExp.setErrors({ regex: true });
      }
    } else {
      this.regex = null;
    }

    this.DefaultValue.updateValueAndValidity();
  }

  private registerFormControlChangesListener() {
    this.RegExp.valueChanges.subscribe((value: string | null) => {
      this.setRegex(value);
    });
  }

  setCountType(base: AllowedValuesCountEnum) {
    this.selectedType = base;
    switch (this.selectedType) {
      case AllowedValuesCountEnum.CUSTOM:
      case AllowedValuesCountEnum.AT_LEAST_ONE:
        this.AllowedValuesMaxCount.setValue(null);
        this.AllowedValuesMinCount.setValue(1);
        break;
      case AllowedValuesCountEnum.AT_MOST_ONE:
        this.AllowedValuesMaxCount.setValue(1);
        this.AllowedValuesMinCount.setValue(null);
        break;
      case AllowedValuesCountEnum.EXACT_ONE:
        this.AllowedValuesMaxCount.setValue(1);
        this.AllowedValuesMinCount.setValue(1);
        break;
      case AllowedValuesCountEnum.ANY:
      default:
        this.AllowedValuesMaxCount.setValue(null);
        this.AllowedValuesMinCount.setValue(null);
        break;
    }
    this.enableDisableAllowedValuesCountInputs();
  }

  private enableDisableAllowedValuesCountInputs() {
    if (this.selectedType === AllowedValuesCountEnum.CUSTOM) {
      this.AllowedValuesMaxCount.enable();
      this.AllowedValuesMinCount.enable();
    } else {
      this.AllowedValuesMaxCount.disable();
      this.AllowedValuesMinCount.disable();
    }
  }

  removeKeyword(keyword: string) {
    const index = this.attribute.allowedValues.indexOf(keyword);
    if (index < 0) {
      return;
    }

    this.attribute.allowedValues.splice(index, 1);
  }

  onKeyboardEnter(event: KeyboardEvent) {
    event.preventDefault();
    event.stopPropagation();
    console.log(event);
    this.add(event.target as HTMLInputElement);
  }

  add(event: HTMLInputElement): void {
    const value = (event.value || '').trim();
    if (!value) {
      return;
    }

    if (!this.attribute.allowedValues) {
      this.attribute.allowedValues = [];
    }

    // Add our keyword
    this.attribute.allowedValues.push(value);

    // Clear the input value
    event.value = '';
  }
}
